import { Formik, FormikHelpers } from "formik"

import { NotificationContext } from "../../../../notifications/context"

import PillButton from "@components/elements/V2/Buttons/PillButton"
import DropdownInput, {
  SelectOption,
} from "@components/elements/V2/Inputs/DropdownInput"
import TextInput from "@components/elements/V2/Inputs/TextInput"
import Typography from "@components/elements/V2/Typography"
import GridSection from "@components/V2/Layouts/GridSection"
import { KNOWLEDGE_HUB_SUGGESTION } from "@constants/segmentEvents"
import { Color } from "@constants/V2/color"
import { trackSegmentEvent } from "@utils/analytics"
import { getAccentColor } from "@utils/V2/color"
import React, { useContext } from "react"

const TEXT_COLOR = Color.Charcoal

const CATEGORY_OPTIONS: SelectOption[] = [
  "Card machine",
  "Point of sale",
  "Online",
  "Other",
].map((category) => ({ label: category, value: category }))

type SuggestionValues = {
  category: string
  suggestion: string
  name: string
  location: string
}

const INITIAL_SUGGESTION_VALUES: SuggestionValues = {
  category: "",
  suggestion: "",
  name: "",
  location: "",
}

const isCompleteSuggestion = ({ category, suggestion }: SuggestionValues) =>
  category !== "" && suggestion.trim() !== ""

const ArticleSuggestionSection = (
  props: React.DOMAttributes<HTMLDivElement>
) => {
  const { setNotification } = useContext(NotificationContext)

  const submitSuggestion = (
    values: SuggestionValues,
    { resetForm }: FormikHelpers<SuggestionValues>
  ) => {
    if (!isCompleteSuggestion(values)) {
      return
    }

    const name = values.name.trim()
    const location = values.location.trim()

    trackSegmentEvent(KNOWLEDGE_HUB_SUGGESTION, {
      category: values.category,
      suggestion: values.suggestion.trim(),
      ...(name ? { name } : {}),
      ...(location ? { location } : {}),
    })
    setNotification({
      type: "success",
      title:
        "Thanks for your suggestion. It will help shape what we cover next.",
    })
    resetForm()
  }

  return (
    <GridSection primaryBackgroundColor={getAccentColor(TEXT_COLOR)} {...props}>
      <div className="col-span-full space-y-24 lg:col-span-8 lg:col-start-3">
        <Typography
          element="h2"
          size="h4"
          font="grotesk"
          weight="medium"
          color={TEXT_COLOR}
          text="What would you like us to explain?"
          className="text-center"
        />

        <Formik
          initialValues={INITIAL_SUGGESTION_VALUES}
          onSubmit={submitSuggestion}
        >
          {({ values, handleSubmit }) => (
            <form
              onSubmit={handleSubmit}
              className="relative z-20 w-full space-y-16"
            >
              <div className="flex flex-col gap-16 md:flex-row">
                <DropdownInput
                  id="suggestion-category"
                  name="category"
                  placeholder="Category"
                  options={CATEGORY_OPTIONS}
                  color={TEXT_COLOR}
                  className="md:w-1/4 md:flex-none"
                  isRounded
                />
                <TextInput
                  id="suggestion"
                  name="suggestion"
                  type="text"
                  placeholder="Enter your question or topic"
                  color={TEXT_COLOR}
                  className="md:flex-1"
                  isRounded
                />
              </div>
              <div className="flex flex-col gap-16 md:flex-row md:items-center">
                <TextInput
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Name (optional)"
                  color={TEXT_COLOR}
                  className="md:flex-1"
                  isRounded
                />
                <TextInput
                  id="location"
                  name="location"
                  type="text"
                  placeholder="Location (optional)"
                  color={TEXT_COLOR}
                  className="md:flex-1"
                  isRounded
                />
                <PillButton
                  type="submit"
                  style="solid"
                  size="large"
                  color={Color.Blue}
                  text="Suggest a topic"
                  disabled={!isCompleteSuggestion(values)}
                  className="self-center md:flex-none"
                />
              </div>
            </form>
          )}
        </Formik>
      </div>
    </GridSection>
  )
}

export default ArticleSuggestionSection
